
import { useForm } from "react-hook-form";
import { useNavigate, useBeforeUnload } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { Form } from "@/components/ui/form";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FormFields } from "./form/FormFields";
import { FormActions } from "./form/FormActions";
import { ProfileFormData, profileFormSchema } from "./types";
import { zodResolver } from "@hookform/resolvers/zod";
import { ProfileFormSkeleton } from "./sections/ProfileFormSkeleton";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState, useEffect } from "react";

const ProfileForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session;
    },
  });

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', session?.user?.id],
    enabled: !!session?.user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session?.user?.id)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching profile:', error);
        throw error;
      }
      return data;
    },
  });

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      bio: "",
      city: "",
      country: "",
      languages: "",
      hosting_style: [],
      specialties: "",
      nationality: "",
    },
  });

  useEffect(() => {
    if (profile) {
      console.log("Setting form values from profile:", profile);
      
      // Process hosting_style to ensure it's always a proper array
      const processHostingStyle = (): string[] => {
        if (!profile.hosting_style) return [];
        
        // If it's already an array, return it
        if (Array.isArray(profile.hosting_style)) return profile.hosting_style;
        
        // If it's a string that looks like JSON array
        if (typeof profile.hosting_style === 'string') {
          if (profile.hosting_style.startsWith('[') && profile.hosting_style.endsWith(']')) {
            try {
              const parsed = JSON.parse(profile.hosting_style);
              if (Array.isArray(parsed)) return parsed;
            } catch (e) {
              // If parsing fails, treat as a single string
            }
          }
          // If it's a regular string or JSON parsing failed
          return [profile.hosting_style];
        }
        
        return [];
      };
      
      form.reset({
        bio: profile.bio || "",
        city: profile.location?.split(', ')[0] || "",
        country: profile.location?.split(', ')[1] || "",
        languages: profile.languages?.join(", ") || "",
        hosting_style: processHostingStyle(),
        specialties: profile.specialties?.join(", ") || "",
        nationality: profile.nationality || "",
      });
    }
  }, [profile, form]);

  useEffect(() => {
    const subscription = form.watch(() => {
      setIsDirty(form.formState.isDirty);
    });
    return () => subscription.unsubscribe();
  }, [form.watch]);

  useBeforeUnload(
    (event) => {
      if (isDirty) {
        event.preventDefault();
        return (event.returnValue = "You have unsaved changes. Are you sure you want to leave?");
      }
    }
  );

  const onSubmit = async (data: ProfileFormData) => {
    setIsSubmitting(true);
    try {
      console.log('Submitting form with data:', data);
      
      const updateData = {
        bio: data.bio,
        location: `${data.city}, ${data.country}`,
        languages: data.languages.split(",").map(lang => lang.trim()).filter(Boolean),
        nationality: data.nationality,
        hosting_style: data.hosting_style,
        specialties: data.specialties?.split(",").map(spec => spec.trim()).filter(Boolean),
      };

      console.log('Sending update with data:', updateData);

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', session?.user?.id);

      if (error) {
        console.error('Error during profile update:', error);
        throw error;
      }

      await queryClient.invalidateQueries({ queryKey: ['profile', session?.user?.id] });

      toast({
        title: "Success",
        description: "Your profile has been updated successfully.",
        className: "animate-fade-in",
      });

      setIsDirty(false);
      navigate('/profile');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
        className: "animate-fade-in",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavigateBack = () => {
    if (isDirty) {
      setShowDiscardDialog(true);
    } else {
      navigate('/profile');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <Button 
          variant="ghost" 
          className="mb-6" 
          onClick={handleNavigateBack}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Profile
        </Button>
        
        <h1 className="text-2xl font-bold mb-6">Edit Profile</h1>

        {isLoading ? (
          <ProfileFormSkeleton />
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormFields form={form} />
              <FormActions 
                isSubmitting={isSubmitting} 
                onCancel={handleNavigateBack}
              />
            </form>
          </Form>
        )}

        <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
          <AlertDialogContent className="animate-fade-in">
            <AlertDialogHeader>
              <AlertDialogTitle>Discard Changes?</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes. Are you sure you want to discard them?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setShowDiscardDialog(false);
                  navigate('/profile');
                }}
              >
                Discard Changes
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default ProfileForm;
